import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EndUser, getIITLib, loadIITLib, resetIITLib } from '~/libs/iit/lib.js';
import { RootState } from '~/store/store.ts';
import ModalAlert from '~/components/modals/ModalAlert/ModalAlert';
import {
  dismissIITError,
  IITCloudVerification,
  IITPrivateKeyLoaded,
  setIITError,
  setIITLoading,
} from '~/store/iit/slices.ts';
import { errorMessages } from '~/constants/errorMessages.ts';
import CloudQRVerification from './CloudQRVerification.tsx';
import { TIITSign } from '~/store/iit/types.ts';
import { TDocument } from '~/modules/identification/forms/Documents/types.ts';
import DocumentsSection from '~/modules/identification/forms/Documents/components/DocumentSection/DocumentsSection.tsx';
import * as Sentry from '@sentry/react';
import { Box, Button, Grid, Pagination, Typography } from '@mui/material';
import { styles } from '~/components/IITSign/styles.ts';
import type { TimerHandle } from '~/components/Timer/Timer.tsx';

interface IITSignPagination {
  page: number;
  pagesCount: number;
  onPageChange: (page: number) => void;
}

interface IITSignProps {
  signDocs: (signFiles: TIITSign[]) => void;
  documents: TDocument[];
  handlePreview?: (id: number | string) => void;
  pagination?: IITSignPagination;
}

const IITSign = ({ signDocs, documents, handlePreview, pagination }: IITSignProps) => {
  const dispatch = useDispatch();

  const SIGN_WIDGET_PARENT_ID = 'sign-widget-parent';
  const SIGN_WIDGET_ID = 'sign-widget';
  const SIGN_WIDGET_URI = import.meta.env.VITE_SIGN_WIDGET_URI;

  const timerRef = useRef<TimerHandle | null>(null);

  const {
    isSigning,
    hasError,
    error,
    IITHashes,
    isPrivateKeyLoaded,
    isCloudKeyQRVerificationModalOpen,
    cloudKeyQRVerificationURL,
  } = useSelector((state: RootState) => state.iit) || {};

  const closeModalAlert = () => {
    dispatch(dismissIITError());
  };

  useEffect(() => {
    if (isCloudKeyQRVerificationModalOpen) {
      timerRef.current?.resetTimer();
      timerRef.current?.startTimer();
    } else {
      timerRef.current?.resetTimer();
    }
  }, [isCloudKeyQRVerificationModalOpen]);

  function onCloudSignRequest(signRequestEvent: { url: string }) {
    dispatch(IITCloudVerification(signRequestEvent.url));
  }

  function onSign() {
    try {
      let previousSign = null;
      let asBase64String = true;
      let signAlgo = EndUser.SignAlgo.DSTU4145WithGOST34311;
      let signType = EndUser.SignType.CAdES_X_Long | EndUser.SignType.CAdES_X_Long_Trusted;

      let hashesToSign = (IITHashes || []).map(
        (doc: { fileHashToSign: string }) => doc.fileHashToSign
      );
      let fileNamesToSign = (IITHashes || []).map(
        (doc: { fileNameToSign: string }) => doc.fileNameToSign
      );
      dispatch(setIITLoading(true));
      Sentry.logger.info('IITSign', { action: 'Start IIT signing' });

      getIITLib()
        .SignHash(hashesToSign, asBase64String, signAlgo, signType, previousSign)
        .then(function (sign: string[]) {
          let signFiles = [];
          for (let i = 0; i < sign.length; i++) {
            signFiles[i] = {
              base64File: sign[i] || '',
              documentId: IITHashes?.[i]?.documentId,
              name: fileNamesToSign[i] + '.p7s',
            };
          }
          Sentry.logger.info('IITSign', { action: 'SignHash/then', files: signFiles });
          return signDocs(signFiles);
        })
        .catch((error: unknown) => {
          Sentry.captureException(error);
          dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
        });
    } catch (error) {
      Sentry.captureException(error);
      dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
    }
  }

  useEffect(() => {
    if (isSigning && !getIITLib()) {
      try {
        loadIITLib(
          SIGN_WIDGET_PARENT_ID,
          SIGN_WIDGET_ID,
          SIGN_WIDGET_URI,
          EndUser.FormType.ReadPKey
        );
        Sentry.logger.info('IITSign', { action: 'Load IIT lib' });

        getIITLib()
          .ReadPrivateKey()
          .then(() => {
            Sentry.logger.info('IITSign', { action: 'getIITLib.ReadPrivateKey' });
            getIITLib().AddEventListener(EndUser.EventType.ConfirmKSPOperation, onCloudSignRequest);
          })
          .then(() => {
            Sentry.logger.info('IITSign', { action: 'getIITLib.ReadPrivateKeyLoaded' });
            dispatch(IITPrivateKeyLoaded());
          })
          .catch((error: unknown) => {
            Sentry.captureException(error);
            resetIITLib();
            dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
          });
      } catch (error) {
        Sentry.captureException(error);
        resetIITLib();
        dispatch(setIITError(errorMessages.SERVICE_UNAVAILABLE));
      }
    }
  }, [isSigning]);

  return (
    <>
      <ModalAlert
        isOpen={hasError}
        onClose={closeModalAlert}
        text={error || errorMessages.SERVICE_UNAVAILABLE}
      />
      <Box sx={{ display: isPrivateKeyLoaded ? 'none' : 'initial' }}>
        <Typography variant="h4" sx={{ textAlign: 'center', paddingBottom: '20px' }}>
          Завантажте свій ключ
        </Typography>
        <Box sx={styles.documentsSignContainer}>
          <Box id="sign-widget-parent" sx={{ width: '100%', height: '400px' }}></Box>
        </Box>
      </Box>

      {isPrivateKeyLoaded && !isCloudKeyQRVerificationModalOpen && (
        <Box>
          <DocumentsSection
            headerText="Перегляньте документи для підписання"
            documents={documents}
            blockId={3}
            isEditable={false}
            fetchPreview={handlePreview}
          />
          {pagination && pagination.pagesCount > 1 && (
            <Grid sx={{ mt: 1 }}>
              <Pagination
                page={pagination.page}
                count={pagination.pagesCount}
                color="primary"
                onChange={(_, page) => pagination.onPageChange(page)}
              />
            </Grid>
          )}
          <Box sx={styles.buttonContainer}>
            <Button onClick={onSign} variant={'contained'} type={'button'}>
              Підписати
            </Button>
          </Box>
        </Box>
      )}

      <CloudQRVerification
        isCloudKeyQRVerificationModalOpen={isCloudKeyQRVerificationModalOpen}
        url={cloudKeyQRVerificationURL}
        timerRef={timerRef}
      />
    </>
  );
};

export default IITSign;
